import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Shield, FileText, Briefcase, ArrowRight, CheckCircle,
  FolderKanban, TrendingUp, Building2, Zap, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Logo } from '../components/Logo';
import { useForcedDarkMode } from '../contexts/ThemeContext';

// Grid Background Component
const GridBackground = () => (
  <div className="absolute inset-0 overflow-hidden" style={{ contain: 'strict' }}>
    <svg className="absolute inset-0 w-full h-full opacity-20" aria-hidden="true">
      <defs>
        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#2D7D6F" strokeWidth="0.5"/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" />
    </svg>
    {/* Optimized glow - uses CSS gradient blur instead of filter:blur for GPU acceleration */}
    <div 
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[800px] rounded-full opacity-20 pointer-events-none"
      style={{ 
        background: 'radial-gradient(ellipse, rgba(0,229,160,0.4) 0%, rgba(0,229,160,0.1) 30%, transparent 70%)',
        willChange: 'transform',
        contain: 'layout style paint'
      }}
      aria-hidden="true"
    />
  </div>
);

const BusinessSuiteLandingPage = () => {
  useForcedDarkMode();

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, []);

  const modules = [
    {
      id: 'grc',
      icon: Shield,
      title: 'GRC',
      tagline: 'Compliance that actually works.',
      link: '/solutions/grc',
      cta: 'See how GRC works'
    },
    {
      id: 'pmo',
      icon: FolderKanban,
      title: 'PMO',
      tagline: 'Projects you can actually track.',
      link: '/solutions/pmo',
      cta: 'See how PMO works'
    },
    {
      id: 'contracts',
      icon: FileText,
      title: 'Contracts',
      tagline: 'Contracts that stay alive.',
      link: '/solutions/contracts',
      cta: 'See how Contracts work'
    },
    {
      id: 'ma',
      icon: TrendingUp,
      title: 'M&A',
      tagline: 'Due diligence with clarity.',
      link: '/solutions/ma',
      cta: 'See how M&A works'
    },
    {
      id: 'cosec',
      icon: Building2,
      title: 'Company Secretary',
      tagline: 'Governance made simple.',
      link: '/solutions/company-secretary',
      cta: 'See how Company Secretary works'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0f0d] text-white">
      <Helmet>
        <title>Business Management Platform: GRC, PMO & Contracts | Simplif-i</title>
        <meta name="description" content="Unified business management platform combining GRC, PMO, contract management, M&A and company secretary. Replace disconnected tools with one system. Modules from £49/month. Start free trial." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://simplif-i.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://simplif-i.com/" />
        <meta property="og:title" content="Business Management Platform: GRC, PMO & Contracts | Simplif-i" />
        <meta property="og:description" content="One platform for governance, projects, contracts and deals. Built for how businesses actually run." />
        <meta property="og:image" content="https://simplif-i.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="keywords" content="business management platform, GRC software, PMO software, contract management, M&A software, company secretary, governance software, compliance platform UK" />
        <script type="application/ld+json">{`{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Simplif-i",
          "applicationCategory": "BusinessApplication",
          "offers": {
            "@type": "AggregateOffer",
            "lowPrice": "49",
            "highPrice": "499",
            "priceCurrency": "GBP"
          },
          "description": "Unified business management platform for GRC, PMO, Contracts, M&A and Company Secretary",
          "operatingSystem": "Web-based"
        }`}</script>
      </Helmet>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0f0d]/90 backdrop-blur-sm border-b border-[#2D7D6F]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Logo className="h-8 w-auto" showText={false} />
              <span className="text-xl font-semibold text-white">Simplif-i</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-6">
              <Link to="/pricing" className="text-white/70 hover:text-[#2D7D6F] transition-colors text-sm font-medium">
                Pricing
              </Link>
              <Link to="/solutions/grc" className="text-white/70 hover:text-[#2D7D6F] transition-colors text-sm font-medium">
                Solutions
              </Link>
            </div>
            
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-white hover:text-[#2D7D6F] transition-colors text-sm font-medium">
                Login
              </Link>
              <Link to="/signup">
                <Button className="bg-[#2D7D6F] hover:bg-[#2D7D6F]/90 text-[#0a0f0d] font-semibold px-5">
                  Start free trial
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* SECTION 1 — HERO */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden pt-16">
        <GridBackground />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
          {/* H1 - includes "business management platform" for SEO */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight">
            <span className="sr-only">Business Management Platform: </span>
            Run your business on one system.
            <br />
            <span className="text-[#2D7D6F]">Not five.</span>
          </h1>
          
          {/* H2 */}
          <h2 className="text-xl sm:text-2xl text-white/80 mb-4 font-medium">
            Governance. Projects. Contracts. Deals. One platform.
          </h2>
          
          {/* Supporting copy */}
          <p className="text-base sm:text-lg text-white/70 mb-8 max-w-2xl mx-auto">
            Replace disconnected tools with a single operating layer for how your business actually runs.
          </p>
          
          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <Link to="/signup" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto bg-[#2D7D6F] hover:bg-[#2D7D6F]/90 text-[#0a0f0d] font-semibold h-14 px-8 text-base rounded-lg">
                Start free trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/pricing" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/30 text-white hover:bg-white/5 h-14 px-8 text-base rounded-lg">
                View pricing
              </Button>
            </Link>
          </div>
          
          {/* Pricing anchor */}
          <div className="inline-flex flex-col items-center gap-1 px-6 py-3 rounded-xl bg-[#2D7D6F]/5 border border-[#2D7D6F]/20">
            <p className="text-white/50 text-sm line-through">&pound;499/month</p>
            <p className="text-[#2D7D6F] font-semibold text-lg">
              Full platform &pound;149/month <span className="text-[#2D7D6F]/80 font-normal text-sm">(Founding Member)</span>
            </p>
            <p className="text-white/70 text-sm">Or start with modules from &pound;49/month</p>
          </div>
        </div>
      </section>

      {/* SECTION 2 — PROBLEM */}
      <section className="py-20 bg-[#0d1210] border-y border-[#2D7D6F]/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-10 text-white">
            You already know the problem.
          </h2>
          
          <div className="space-y-6 text-lg">
            <p className="text-white/70">
              Compliance in SharePoint.
              <br />
              Contracts in folders.
              <br />
              Projects in another tool.
            </p>
            
            <div className="pt-4 border-t border-white/10 mt-8">
              <p className="text-white/70">
                The board asks one question.
                <br />
                <span className="text-white/80">You spend two days answering it.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — PLATFORM (NEW) */}
      <section className="py-20 relative overflow-hidden">
        <GridBackground />
        
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6">
            One platform. <span className="text-[#2D7D6F]">Everything connected.</span>
          </h2>
          
          <div className="space-y-6 text-lg text-white/70 mb-10">
            <p>Everything connects.</p>
          </div>
          
          <div className="grid gap-4 max-w-xl mx-auto text-left">
            <div className="flex items-center gap-4 p-4 bg-[#2D7D6F]/5 rounded-lg border border-[#2D7D6F]/10">
              <Zap className="h-5 w-5 text-[#2D7D6F] shrink-0" />
              <p className="text-white/80">Risk links to projects.</p>
            </div>
            <div className="flex items-center gap-4 p-4 bg-[#2D7D6F]/5 rounded-lg border border-[#2D7D6F]/10">
              <Zap className="h-5 w-5 text-[#2D7D6F] shrink-0" />
              <p className="text-white/80">Contracts link to delivery.</p>
            </div>
            <div className="flex items-center gap-4 p-4 bg-[#2D7D6F]/5 rounded-lg border border-[#2D7D6F]/10">
              <Zap className="h-5 w-5 text-[#2D7D6F] shrink-0" />
              <p className="text-white/80">Governance reflects reality.</p>
            </div>
          </div>
          
          <p className="text-white/70 mt-8 text-base">
            No silos. No syncing. No second version of the truth.
          </p>
        </div>
      </section>

      {/* SECTION 4 — MODULES (SOLUTIONS) */}
      <section className="py-20 bg-[#0d1210] border-y border-[#2D7D6F]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Five modules. <span className="text-[#2D7D6F]">One platform.</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {modules.map((module) => (
              <Card key={module.id} className="bg-[#0a0f0d] border-[#2D7D6F]/20 hover:border-[#2D7D6F]/40 transition-all">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 rounded-lg bg-[#2D7D6F]/10">
                      <module.icon className="h-5 w-5 text-[#2D7D6F]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#2D7D6F]">{module.title}</h3>
                  </div>
                  <p className="text-white/70 text-sm mb-5">{module.tagline}</p>
                  <Link 
                    to={module.link} 
                    className="text-[#2D7D6F] hover:text-[#2D7D6F]/80 font-medium inline-flex items-center text-sm"
                  >
                    {module.cta} <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — PRICING */}
      <section className="py-20 relative overflow-hidden">
        <GridBackground />
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Start with one system. <span className="text-[#2D7D6F]">Or run everything.</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Full Platform - Primary */}
            <Card className="bg-[#2D7D6F]/5 border-[#2D7D6F] border-2 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-[#2D7D6F] text-[#0a0f0d] text-xs font-bold px-3 py-1 rounded-full">
                  FOUNDING MEMBER
                </span>
              </div>
              <CardContent className="p-8 pt-10">
                <h3 className="text-xl font-bold text-white mb-2">Full Platform</h3>
                <div className="mb-1">
                  <span className="text-white/50 line-through text-xl">&pound;499</span>
                </div>
                <div className="mb-2">
                  <span className="text-4xl font-bold text-[#2D7D6F]">&pound;149</span>
                  <span className="text-white/70">/month</span>
                </div>
                <p className="text-[#2D7D6F]/80 text-xs mb-4">Founding Member pricing</p>
                <p className="text-white/70 text-sm mb-4">
                  Everything included.<br />
                  Governance, projects, contracts and deals — all in one system.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-white/70 text-sm">
                    <CheckCircle className="h-4 w-4 text-[#2D7D6F]" />
                    GRC
                  </li>
                  <li className="flex items-center gap-2 text-white/70 text-sm">
                    <CheckCircle className="h-4 w-4 text-[#2D7D6F]" />
                    PMO
                  </li>
                  <li className="flex items-center gap-2 text-white/70 text-sm">
                    <CheckCircle className="h-4 w-4 text-[#2D7D6F]" />
                    Contracts
                  </li>
                  <li className="flex items-center gap-2 text-white/70 text-sm">
                    <CheckCircle className="h-4 w-4 text-[#2D7D6F]" />
                    M&amp;A
                  </li>
                  <li className="flex items-center gap-2 text-white/70 text-sm">
                    <CheckCircle className="h-4 w-4 text-[#2D7D6F]" />
                    Company Secretary
                  </li>
                </ul>
                <Link to="/signup?plan=full" className="block">
                  <Button className="w-full bg-[#2D7D6F] hover:bg-[#2D7D6F]/90 text-[#0a0f0d] font-semibold h-12">
                    Get full platform <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
            
            {/* Modules - Entry */}
            <Card className="bg-[#0a0f0d] border-[#2D7D6F]/20">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-white mb-2">Or start smaller</h3>
                <div className="mb-4">
                  <span className="text-white/50 text-sm">Modules from</span>
                  <div>
                    <span className="text-4xl font-bold text-white">&pound;49</span>
                    <span className="text-white/70">/month</span>
                  </div>
                </div>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center justify-between text-white/70 text-sm">
                    <span className="flex items-center gap-2">
                      <FolderKanban className="h-4 w-4 text-[#2D7D6F]" />
                      PMO
                    </span>
                    <span>&pound;49</span>
                  </li>
                  <li className="flex items-center justify-between text-white/70 text-sm">
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-[#2D7D6F]" />
                      Contracts
                    </span>
                    <span>&pound;49</span>
                  </li>
                  <li className="flex items-center justify-between text-white/70 text-sm">
                    <span className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-[#2D7D6F]" />
                      Company Secretary
                    </span>
                    <span>&pound;49</span>
                  </li>
                  <li className="flex items-center justify-between text-white/70 text-sm">
                    <span className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-[#2D7D6F]" />
                      M&A
                    </span>
                    <span>&pound;99</span>
                  </li>
                  <li className="flex items-center justify-between text-white/70 text-sm">
                    <span className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-[#2D7D6F]" />
                      GRC
                    </span>
                    <span><span className="line-through text-white/40">&pound;299</span> <span className="text-[#2D7D6F]">&pound;149</span></span>
                  </li>
                </ul>
                <Link to="/pricing" className="block">
                  <Button variant="outline" className="w-full border-[#2D7D6F]/30 text-white hover:bg-[#2D7D6F]/5 h-12">
                    Explore modules
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
          
          {/* Conversion nudge */}
          <p className="text-center text-white/60 text-sm mt-8">
            Using multiple modules? <span className="text-[#2D7D6F]">Full platform is better value.</span>
          </p>
        </div>
      </section>

      {/* SECTION 6 — FINAL CTA */}
      <section className="py-24 bg-[#0d1210] border-t border-[#2D7D6F]/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Stop managing systems.
            <br />
            <span className="text-[#2D7D6F]">Start running your business.</span>
          </h2>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            <Link to="/signup" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto bg-[#2D7D6F] hover:bg-[#2D7D6F]/90 text-[#0a0f0d] font-semibold h-14 px-10 text-base rounded-lg">
                Start free trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
          
          <p className="text-white/70 text-sm mt-6">
            7-day free trial · Full Pro access · No credit card required
          </p>
        </div>
      </section>

      {/* SECTION 7 — WHY SIMPLIF-I (SEO Text Content) */}
      <section className="py-16 bg-[#0a0f0d]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">
            Why businesses choose Simplif-i
          </h2>
          
          <div className="prose prose-invert max-w-none text-white/70 space-y-4">
            <p>
              Simplif-i is a comprehensive business management platform designed for organisations that need to manage governance, risk, compliance, projects, contracts, and corporate administration without the complexity of multiple disconnected tools. Built by a certified ISO Lead Auditor who has run these functions operationally, Simplif-i understands what businesses actually need day-to-day.
            </p>
            
            <p>
              The platform supports over 30 compliance frameworks including ISO 27001, SOC 2 Type I and Type II, GDPR, UK GDPR, Cyber Essentials, Cyber Essentials Plus, HIPAA, PCI DSS, NIST CSF, and many more. Evidence collected for one framework automatically maps to overlapping controls in others, eliminating the duplication that plagues traditional GRC tools.
            </p>
            
            <p>
              Unlike enterprise software that requires months of implementation, Simplif-i works from day one. Upload your existing policies and the AI parses them, extracts evidence, and maps it to your chosen frameworks. Where gaps exist, the Policy Architect generates compliant documentation tailored to your business. The Evidence Collector captures screenshots from any URL without requiring API integrations or IT involvement.
            </p>
            
            <p>
              For company secretaries, Simplif-i tracks statutory filing deadlines, manages board packs, and maintains corporate registers. For project managers, the PMO module provides portfolio oversight, resource management, and delivery tracking. For legal teams, the Contracts module handles the entire contract lifecycle from template creation to renewal management.
            </p>
            
            <p>
              One full-platform plan at a founding price of £149/month or £1,490/year (12 months for the price of 10), locked in while your subscription stays active (usual price £499/month). It includes a 7-day free trial; card details are taken at checkout and you are charged on day 8 unless you cancel.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-10 text-center">
            <div>
              <div className="text-3xl font-bold text-[#2D7D6F]">30+</div>
              <div className="text-white/60 text-sm">Compliance frameworks</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#2D7D6F]">£149</div>
              <div className="text-white/60 text-sm">Founding price per month</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#2D7D6F]">7-day</div>
              <div className="text-white/60 text-sm">Free trial</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#2D7D6F]">0</div>
              <div className="text-white/60 text-sm">Implementation time</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-[#2D7D6F]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="md:col-span-1">
              <Link to="/" className="flex items-center gap-2 mb-4">
                <Logo className="h-6 w-auto" showText={false} />
                <span className="text-lg font-semibold text-white">Simplif-i</span>
              </Link>
              <p className="text-white/70 text-sm">
                One platform for how your business actually runs.
              </p>
            </div>
            
            {/* Solutions */}
            <div>
              <h3 className="text-white font-semibold mb-4 text-sm">Solutions</h3>
              <ul className="space-y-2 text-sm text-white/70">
                <li><Link to="/solutions/grc" className="hover:text-[#2D7D6F] transition-colors">GRC</Link></li>
                <li><Link to="/solutions/pmo" className="hover:text-[#2D7D6F] transition-colors">PMO</Link></li>
                <li><Link to="/solutions/contracts" className="hover:text-[#2D7D6F] transition-colors">Contracts</Link></li>
                <li><Link to="/solutions/ma" className="hover:text-[#2D7D6F] transition-colors">M&A</Link></li>
                <li><Link to="/solutions/company-secretary" className="hover:text-[#2D7D6F] transition-colors">Company Secretary</Link></li>
              </ul>
            </div>
            
            {/* Company */}
            <div>
              <h3 className="text-white font-semibold mb-4 text-sm">Resources</h3>
              <ul className="space-y-2 text-sm text-white/70">
                <li><Link to="/blog" className="hover:text-[#2D7D6F] transition-colors">Blog</Link></li>
                <li><Link to="/pricing" className="hover:text-[#2D7D6F] transition-colors">Pricing</Link></li>
                <li><Link to="/contact" className="hover:text-[#2D7D6F] transition-colors">Contact</Link></li>
              </ul>
            </div>
            
            {/* Legal */}
            <div>
              <h3 className="text-white font-semibold mb-4 text-sm">Legal</h3>
              <ul className="space-y-2 text-sm text-white/70">
                <li><Link to="/privacy" className="hover:text-[#2D7D6F] transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-[#2D7D6F] transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-[#2D7D6F]/10 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/60">
              &copy; 2026 Simplif-i Systems Ltd. All rights reserved.
            </p>
            <a 
              href="https://sellwithboost.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img 
                src="https://sellwithboost.com/badge/listing.svg" 
                alt="Listed on Sell With Boost" 
                style={{ height: '40px', width: 'auto' }} 
              />
            </a>
            <p className="text-xs text-white/60">
              Simplif-i Systems Ltd, 66 Paul Street, London, EC2A 4NA
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BusinessSuiteLandingPage;
